import { useState, useEffect } from "react"
import { BracketMatchup, BracketRound, BracketTeam } from "../types/bracket"
import { cn } from "../lib/utils"
import { useCompetitionRounds } from "../hooks/useCompetitionRounds"
import type { MatchupPrediction, PicksOrderPrefill } from "../pages/PicksPage"
import { TeamCodeBadge } from "./ui/TeamCodeBadge"
import { TEAM_IDENTITY_BADGE_SIZE, TEAM_IDENTITY_NAME_CLASS } from "./ui/teamIdentityScale"
import { getTeamPickCardStyle } from "../lib/teamPickStyles"
import { getWorldCupFlagCountryCode } from "@sportex100/shared/utils/worldCupFlags"
import { isSoccerTradingCompetition } from "@sportex100/shared/utils/competitionIdentity.ts"

const DESKTOP_ROUND_MIN_WIDTH = 260
const MOBILE_ROUND_WIDTH = "85vw"
const MATCHUP_CARD_BASE_HEIGHT = 96
const SCORE_COL_WIDTH = 32
const PTS_COL_WIDTH = 56
const TEAM_STATS_COL_WIDTH = 104

interface BracketTreeProps {
  rounds: BracketRound[]
  competitionCode: string
  scenarioMode?: boolean
  predictions?: Map<string, MatchupPrediction>
  onPredictionChange?: (seriesCode: string, prediction: MatchupPrediction | null) => void
  teamScenarioPoints?: Map<string, number>
  pointsPerRound?: Array<{ gameWin: number; roundBonus: number }>
  onOpenOrderForm?: (prefill: PicksOrderPrefill) => void
}

interface MatchupCardProps {
  matchup: BracketMatchup
  scenarioMode?: boolean
  prediction?: MatchupPrediction
  onPredictionChange?: (prediction: MatchupPrediction | null) => void
  teamScenarioPoints?: Map<string, number>
  winsRequired?: number
  competitionCode: string
  onOpenOrderForm?: (prefill: PicksOrderPrefill) => void
}

interface RoundColumnProps {
  round: BracketRound
  columnIndex: number
  isMobile: boolean
  maxMatchupsInBracket: number
  scenarioMode?: boolean
  predictions?: Map<string, MatchupPrediction>
  onPredictionChange?: (seriesCode: string, prediction: MatchupPrediction | null) => void
  teamScenarioPoints?: Map<string, number>
  winsRequired?: number
  competitionCode: string
  onOpenOrderForm?: (prefill: PicksOrderPrefill) => void
}

type PointValueTone = "filled" | "outlined" | "plainWinner" | "plainLoser"

function MatchupCard({
  matchup,
  scenarioMode,
  prediction,
  onPredictionChange,
  teamScenarioPoints,
  winsRequired = 4,
  competitionCode,
  onOpenOrderForm,
}: MatchupCardProps) {
  const { higherSeed, lowerSeed, winnerTeamCode, status } = matchup
  const isComplete = status === "COMPLETE"
  const isConfirmed = status !== "PENDING"

  const higherWinner = Boolean(
    winnerTeamCode && higherSeed && winnerTeamCode === higherSeed.teamCode,
  )
  const lowerWinner = Boolean(
    winnerTeamCode && lowerSeed && winnerTeamCode === lowerSeed.teamCode,
  )

  // Calculate minimum games based on current series score
  const currentHigherWins = higherSeed?.gamesWon || 0
  const currentLowerWins = lowerSeed?.gamesWon || 0
  const currentTotalGames = currentHigherWins + currentLowerWins

  const handleTeamClick = (teamCode: string) => {
    if (!scenarioMode || !onPredictionChange || isComplete) return

    // Determine which team is being clicked
    const isHigherSeed = teamCode === higherSeed?.teamCode
    const clickedTeamCurrentWins = isHigherSeed ? currentHigherWins : currentLowerWins
    const otherTeamCurrentWins = isHigherSeed ? currentLowerWins : currentHigherWins

    // Calculate minimum games: winner needs winsRequired total wins, loser can have at most (winsRequired-1)
    const winsNeededForClickedTeam = Math.max(0, winsRequired - clickedTeamCurrentWins)
    const minGamesForThisWinner = currentTotalGames + winsNeededForClickedTeam

    // Maximum = minGames + remaining games the loser could win (up to winsRequired-1 total)
    const maxLoserWins = winsRequired - 1
    const maxGamesForThisWinner = minGamesForThisWinner + Math.max(0, maxLoserWins - otherTeamCurrentWins)

    // If clicking the same winner, cycle through valid games, then clear
    if (prediction?.winnerTeamCode === teamCode) {
      const nextGames = prediction.gamesWon + 1
      const maxTotalGames = winsRequired * 2 - 1
      if (nextGames <= Math.min(maxTotalGames, maxGamesForThisWinner)) {
        onPredictionChange({ winnerTeamCode: teamCode, gamesWon: nextGames as 4 | 5 | 6 | 7 })
      } else {
        onPredictionChange(null)
      }
    } else {
      const startGames = Math.max(winsRequired, minGamesForThisWinner) as 1 | 4 | 5 | 6 | 7
      onPredictionChange({ winnerTeamCode: teamCode, gamesWon: startGames })
    }
  }

  const hasPrediction = Boolean(prediction)
  const hasResolvedParticipants = Boolean(higherSeed && lowerSeed)
  const isSoccerPicks = isSoccerTradingCompetition(competitionCode)
  const showSeriesRecord = !isSoccerPicks
  const higherFlagCountryCode =
    isSoccerPicks && higherSeed ? getWorldCupFlagCountryCode(higherSeed.teamCode) : null
  const lowerFlagCountryCode =
    isSoccerPicks && lowerSeed ? getWorldCupFlagCountryCode(lowerSeed.teamCode) : null
  const isPickNow = !hasPrediction && hasResolvedParticipants && status === "PENDING"
  const statusLabel = isPickNow ? "PICK NOW" : status
  const effectiveScoreColWidth = showSeriesRecord ? SCORE_COL_WIDTH : 0
  const headerColumns = showSeriesRecord
    ? `${SCORE_COL_WIDTH}px ${PTS_COL_WIDTH}px`
    : `${PTS_COL_WIDTH}px`
  const mobileRowColumns = showSeriesRecord
    ? `minmax(0, 1fr) ${effectiveScoreColWidth}px ${PTS_COL_WIDTH}px`
    : `minmax(0, 1fr) ${PTS_COL_WIDTH}px`
  const tabletStatsColWidth = showSeriesRecord ? TEAM_STATS_COL_WIDTH : PTS_COL_WIDTH
  const tabletRowColumns = `minmax(0, 1fr) ${tabletStatsColWidth}px`
  const predictedLoserWins = prediction ? Math.max(0, prediction.gamesWon - winsRequired) : 0
  const higherRecord =
    scenarioMode && prediction
      ? prediction.winnerTeamCode === higherSeed?.teamCode
        ? `${winsRequired}-${predictedLoserWins}`
        : `${predictedLoserWins}-${winsRequired}`
      : `${currentHigherWins}-${currentLowerWins}`
  const lowerRecord =
    scenarioMode && prediction
      ? prediction.winnerTeamCode === lowerSeed?.teamCode
        ? `${winsRequired}-${predictedLoserWins}`
        : `${predictedLoserWins}-${winsRequired}`
      : `${currentLowerWins}-${currentHigherWins}`

  const getCardTone = () => {
    if (isPickNow) return "border-warning/60"
    if (isComplete) return "border-border-subtle"
    if (hasPrediction) return "border-brand/85"
    if (!isConfirmed) return "border-warning/50"
    return "border-border-subtle"
  }

  const statusPillTone =
    isPickNow
      ? "border-warning/50 bg-warning/15 text-warning"
      : status === "COMPLETE"
        ? "border-border-subtle/60 bg-background/60 text-foreground-muted"
        : status === "ACTIVE"
          ? "border-success/40 bg-success/10 text-success"
          : "border-warning/40 bg-warning/10 text-warning"

  const getRowTone = (teamCode: string, isWinner: boolean) => {
    if (status === "COMPLETE") {
      return isWinner
        ? "text-foreground"
        : "text-foreground-muted"
    }
    if (prediction?.winnerTeamCode === teamCode) {
      return "text-foreground"
    }
    return "text-foreground"
  }

  const getRowStyle = (team: BracketTeam, isWinner: boolean) => {
    if (status === "COMPLETE") {
      return getTeamPickCardStyle(team.teamPrimaryColor, isWinner ? "winner" : "eliminated")
    }
    if (prediction?.winnerTeamCode === team.teamCode) {
      return getTeamPickCardStyle(team.teamPrimaryColor, "selected")
    }
    return getTeamPickCardStyle(team.teamPrimaryColor)
  }

  const formatPts = (value: number | null | undefined) => {
    if (value == null || Number.isNaN(value)) return "—"
    return value > 0 ? `+${value}` : `${value}`
  }

  const isSeriesLeader = (teamWins: number, opponentWins: number) => teamWins > opponentWins

  const getPointValueTone = (
    teamCode: string,
    teamWins: number,
    opponentWins: number,
    isWinner: boolean,
  ): PointValueTone => {
    if (isComplete) {
      return isWinner ? "plainWinner" : "plainLoser"
    }
    if (prediction) {
      return prediction.winnerTeamCode === teamCode ? "filled" : "outlined"
    }
    return isSeriesLeader(teamWins, opponentWins) ? "filled" : "outlined"
  }

  const pointValueClassNameByTone: Record<PointValueTone, string> = {
    filled: "border border-transparent bg-[#FFB020] text-black hover:bg-[#ffc14d]",
    outlined:
      "border border-[rgba(245,166,35,0.40)] bg-[rgba(245,166,35,0.10)] text-[#FFB020] hover:bg-[rgba(245,166,35,0.18)]",
    plainWinner: "text-[#FFB020]",
    plainLoser: "text-[rgba(245,166,35,0.50)]",
  }

  const handleValuePillClick = (teamCode: string, pointValue: number | null | undefined) => {
    onOpenOrderForm?.({
      side: "BUY",
      teamCode,
      price: typeof pointValue === "number" && Number.isFinite(pointValue) ? pointValue : undefined,
    })
  }

  const renderPointValue = (
    team: BracketTeam,
    teamWins: number,
    opponentWins: number,
    isWinner: boolean,
  ) => {
    const pointValue = teamScenarioPoints?.get(`${team.teamCode}_${matchup.seriesCode}`)
    const tone = getPointValueTone(team.teamCode, teamWins, opponentWins, isWinner)
    const label = formatPts(pointValue)

    if (tone === "plainWinner" || tone === "plainLoser") {
      return (
        <span
          className={cn(
            "justify-self-end text-right text-sm font-bold leading-none tabular-nums md:text-[14px]",
            pointValueClassNameByTone[tone],
          )}
        >
          {label}
        </span>
      )
    }

    return (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          handleValuePillClick(team.teamCode, pointValue)
        }}
        className={cn(
          "justify-self-end rounded-[6px] px-[9px] py-[3px] text-xs font-bold leading-none tabular-nums transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand",
          pointValueClassNameByTone[tone],
        )}
        aria-label={`Trade ${team.teamName || team.shortName || team.teamCode}`}
      >
        {label}
      </button>
    )
  }

  const renderPlaceholderContent = (label: string) => (
    <>
      <div className="flex min-w-0 items-center gap-1.5">
        <div className="flex h-[38px] w-[38px] items-center justify-center rounded-md border border-warning/35 bg-warning/10 text-xs font-bold uppercase tracking-[0.12em] text-warning">
          ?
        </div>
        <span className="min-w-0 truncate font-display text-[13px] font-bold uppercase leading-none tracking-[0.035em] text-warning md:text-[14px]">
          {label}
        </span>
      </div>
      {showSeriesRecord && (
        <span className="text-center text-sm font-semibold tabular-nums text-foreground-muted md:text-[15px]">
          —
        </span>
      )}
      <span className="text-right text-sm font-bold tabular-nums text-foreground-muted md:text-[15px]">
        —
      </span>
    </>
  )

  const renderTeamRow = ({
    team,
    placeholderLabel,
    isWinner,
    record,
    teamWins,
    opponentWins,
    flagCountryCode,
  }: {
    team: BracketTeam | null
    placeholderLabel: string | null | undefined
    isWinner: boolean
    record: string
    teamWins: number
    opponentWins: number
    flagCountryCode: string | null
  }) => {
    if (!team && !placeholderLabel) return null

    const teamName = team?.teamName || team?.shortName || team?.teamCode || placeholderLabel || "TBD"

    return (
      <div
        className={cn(
          "grid w-full items-center gap-2 overflow-hidden rounded-lg border px-2.5 py-2 text-left transition-all duration-200 md:block md:px-3 md:py-2.5",
          team ? getRowTone(team.teamCode, isWinner) : "border-dashed border-warning/40 bg-warning/5",
          team && scenarioMode && !isComplete && "hover:border-brand/60",
          isComplete && "cursor-default",
        )}
        style={{
          gridTemplateColumns: mobileRowColumns,
          ...(team ? getRowStyle(team, isWinner) : {}),
        }}
        title={isComplete ? "Series complete - cannot modify" : undefined}
      >
        {team ? (
          <>
            <div
              className="flex min-w-0 items-center gap-2.5 md:grid"
              style={{ gridTemplateColumns: tabletRowColumns }}
            >
              <button
                type="button"
                onClick={() => handleTeamClick(team.teamCode)}
                disabled={!scenarioMode || isComplete}
                className={cn(
                  "flex min-w-0 items-center gap-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand",
                  scenarioMode && !isComplete ? "cursor-pointer" : "cursor-default",
                )}
                aria-label={teamName}
              >
                <div className="flex-shrink-0 leading-none">
                  <TeamCodeBadge
                    code={team.teamCode}
                    size={
                      flagCountryCode
                        ? TEAM_IDENTITY_BADGE_SIZE.picker.worldCupBracketBadge
                        : TEAM_IDENTITY_BADGE_SIZE.picker.bracketBadge
                    }
                    primaryColor={team.teamPrimaryColor ?? null}
                    secondaryColor={team.teamSecondaryColor ?? null}
                    variant={flagCountryCode ? "worldCupFlag" : undefined}
                    flagCountryCode={flagCountryCode}
                  />
                </div>
                <span className={TEAM_IDENTITY_NAME_CLASS.pickerBracketTeam}>
                  {teamName}
                </span>
              </button>
              <div
                className={cn(
                  "picksTabletTeamStats hidden grid-rows-[auto_auto] items-end gap-x-2 gap-y-0.5 md:grid",
                  showSeriesRecord ? "grid-cols-[44px_52px]" : "grid-cols-[56px]",
                )}
              >
                {showSeriesRecord && (
                  <span className="text-right font-display text-[8px] font-bold uppercase tracking-[0.12em] text-foreground-faint">
                    W-L
                  </span>
                )}
                <span className="text-right font-display text-[8px] font-bold uppercase tracking-[0.12em] text-foreground-faint">
                  PTS
                </span>
                {showSeriesRecord && (
                  <span
                    className={cn(
                      "text-right text-sm font-semibold tabular-nums md:text-[15px]",
                      status === "COMPLETE" && isWinner ? "text-success" : "text-foreground-muted",
                    )}
                  >
                    {record}
                  </span>
                )}
                {renderPointValue(team, teamWins, opponentWins, isWinner)}
              </div>
            </div>
            {showSeriesRecord && (
              <span
                className={cn(
                  "text-center text-sm font-semibold tabular-nums md:hidden",
                  status === "COMPLETE" && isWinner ? "text-success" : "text-foreground-muted",
                )}
              >
                {record}
              </span>
            )}
            <span className="md:hidden">
              {renderPointValue(team, teamWins, opponentWins, isWinner)}
            </span>
          </>
        ) : (
          renderPlaceholderContent(placeholderLabel || "TBD")
        )}
      </div>
    )
  }

  return (
    <article className={cn(
      "picksMatchupCard flex h-full w-full flex-col gap-3 rounded-xl border bg-background-card p-3 shadow-[0_12px_28px_rgba(0,0,0,0.28)] transition-colors md:p-4",
      getCardTone(),
    )}>
      <div className="flex items-center justify-between px-0.5">
        <span className={cn(
          "rounded-md border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em]",
          statusPillTone,
          isPickNow && "animate-pulse",
        )}>
          {statusLabel}
        </span>
        <div
          className="grid text-[7px] font-semibold uppercase tracking-[0.08em] text-foreground-muted md:hidden"
          style={{ gridTemplateColumns: headerColumns }}
        >
          {showSeriesRecord && <span className="text-center">W-L</span>}
          <span className="text-right">PTS</span>
        </div>
      </div>
      <div className="space-y-2 md:grid md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:items-center md:gap-3 md:space-y-0">
        {renderTeamRow({
          team: higherSeed,
          placeholderLabel: matchup.higherSeedPlaceholderLabel,
          isWinner: higherWinner,
          record: higherRecord,
          teamWins: currentHigherWins,
          opponentWins: currentLowerWins,
          flagCountryCode: higherFlagCountryCode,
        })}
        {(higherSeed || matchup.higherSeedPlaceholderLabel) && (lowerSeed || matchup.lowerSeedPlaceholderLabel) ? (
          <div className="picksMatchupVersus hidden h-8 w-8 items-center justify-center rounded-full border border-border-subtle/60 bg-background/75 font-display text-[10px] font-bold uppercase tracking-[0.08em] text-foreground-muted shadow-sm md:flex">
            VS
          </div>
        ) : null}
        {renderTeamRow({
          team: lowerSeed,
          placeholderLabel: matchup.lowerSeedPlaceholderLabel,
          isWinner: lowerWinner,
          record: lowerRecord,
          teamWins: currentLowerWins,
          opponentWins: currentHigherWins,
          flagCountryCode: lowerFlagCountryCode,
        })}
        {!higherSeed && !lowerSeed && !matchup.higherSeedPlaceholderLabel && !matchup.lowerSeedPlaceholderLabel && (
          <div className="flex items-center justify-center px-2 py-3 text-body font-semibold text-warning">
            TBD
          </div>
        )}
      </div>
    </article>
  )
}

function RoundColumn({
  round,
  columnIndex,
  isMobile,
  maxMatchupsInBracket,
  scenarioMode,
  predictions,
  onPredictionChange,
  teamScenarioPoints,
  winsRequired,
  competitionCode,
  onOpenOrderForm,
}: RoundColumnProps) {
  const matchupGapPx = 12
  const maxDynamicGapPx = 72
  const cardCount = round.matchups.length
  const matchupsTrackMinHeight =
    Math.max(maxMatchupsInBracket, 1) * MATCHUP_CARD_BASE_HEIGHT +
    Math.max(maxMatchupsInBracket - 1, 0) * matchupGapPx
  const totalCardsHeight = Math.max(cardCount, 1) * MATCHUP_CARD_BASE_HEIGHT
  const availableGapHeight = Math.max(0, matchupsTrackMinHeight - totalCardsHeight)
  const computedGapPx =
    cardCount > 1
      ? availableGapHeight / (cardCount - 1)
      : matchupGapPx
  const adaptiveGapPx = isMobile
    ? matchupGapPx
    : Math.min(maxDynamicGapPx, Math.max(matchupGapPx, computedGapPx))

  return (
    <div className={cn(
      "flex h-full flex-col gap-2 py-1 relative",
      isMobile ? "flex-shrink-0" : "w-full min-w-0",
    )}
    style={isMobile ? { width: MOBILE_ROUND_WIDTH } : undefined}>
      <div
        className="flex flex-1 flex-col justify-center"
        style={{
          minHeight: `${matchupsTrackMinHeight}px`,
          rowGap: `${adaptiveGapPx}px`,
        }}
      >
        {round.matchups.map((matchup) => {
          const key = matchup.seriesCode || `${round.roundCode}-${matchup.seriesName}`
          const prediction = predictions?.get(matchup.seriesCode)

          return (
            <div
              key={key}
              className="flex items-stretch justify-start relative"
              style={{ minHeight: `${MATCHUP_CARD_BASE_HEIGHT}px` }}
            >
              <MatchupCard
                matchup={matchup}
                scenarioMode={scenarioMode}
                prediction={prediction}
                onPredictionChange={onPredictionChange ? (pred) => onPredictionChange(matchup.seriesCode, pred) : undefined}
                teamScenarioPoints={teamScenarioPoints}
                winsRequired={winsRequired}
                competitionCode={competitionCode}
                onOpenOrderForm={onOpenOrderForm}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function BracketTree({
  rounds,
  competitionCode,
  scenarioMode,
  predictions,
  onPredictionChange,
  teamScenarioPoints,
  pointsPerRound,
  onOpenOrderForm,
}: BracketTreeProps) {
  // pointsPerRound is passed through for future use in scoring display
  void pointsPerRound
  const { winsRequired: winsRequiredMap } = useCompetitionRounds();
  const [isMobile, setIsMobile] = useState(false)
  
  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  
  if (!rounds || rounds.length === 0) {
    return (
      <div className="rounded-card border border-border-subtle bg-background-surface p-6 text-center text-body text-foreground-muted">
        No bracket data available.
      </div>
    )
  }

  const sortedRounds = [...rounds].sort((a, b) => a.roundSequence - b.roundSequence)
  const totalRoundsCount = sortedRounds.length
  const maxMatchupsInBracket = sortedRounds.reduce(
    (max, round) => Math.max(max, round.matchups.length),
    0,
  )
  
  // Get wins required for first round (assuming all rounds have same wins_required, which is typical)
  const winsRequired = sortedRounds[0]?.roundCode ? winsRequiredMap[sortedRounds[0].roundCode] || 4 : 4;

  return (
    <div className="picksBracketCanvas">
      <div 
        className="overflow-x-auto overflow-y-hidden scroll-smooth md:scroll-auto snap-x snap-mandatory md:snap-none"
      >
        <div
          className={cn(isMobile ? "flex w-fit" : "grid w-full")}
          style={
            isMobile
              ? undefined
              : { gridTemplateColumns: `repeat(${totalRoundsCount}, minmax(${DESKTOP_ROUND_MIN_WIDTH}px, 1fr))` }
          }
        >
          {sortedRounds.map((round, index) => (
            <div
              key={`${round.roundCode ?? round.roundName}-${index}`}
              className={cn(isMobile ? "snap-start" : "h-full min-w-0")}
            >
              <RoundColumn
                round={round}
                columnIndex={index}
                isMobile={isMobile}
                maxMatchupsInBracket={maxMatchupsInBracket}
                scenarioMode={scenarioMode}
                predictions={predictions}
                onPredictionChange={onPredictionChange}
                teamScenarioPoints={teamScenarioPoints}
                winsRequired={winsRequired}
                competitionCode={competitionCode}
                onOpenOrderForm={onOpenOrderForm}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
